const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

type GoogleCredentialResponse = {
  credential?: string;
};

type GoogleIdentityApi = {
  initialize: (config: {
    auto_select?: boolean;
    callback: (response: GoogleCredentialResponse) => void;
    cancel_on_tap_outside?: boolean;
    client_id: string;
    context?: 'signin' | 'signup' | 'use';
    itp_support?: boolean;
    ux_mode?: 'popup' | 'redirect';
  }) => void;
  renderButton: (
    parent: HTMLElement,
    options: {
      shape?: 'rectangular' | 'pill' | 'circle' | 'square';
      size?: 'large' | 'medium' | 'small';
      theme?: 'outline' | 'filled_blue' | 'filled_black';
      type?: 'standard' | 'icon';
      width?: number;
    },
  ) => void;
};

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: GoogleIdentityApi;
      };
    };
  }
}

let loading: Promise<void> | null = null;

export function loadGoogleIdentityScript(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    const finish = () => {
      if (window.google?.accounts?.id) {
        resolve();
        return;
      }
      loading = null;
      reject(new Error('GIS_UNAVAILABLE'));
    };
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      if (window.google?.accounts?.id) {
        resolve();
        return;
      }
      existing.addEventListener('load', finish, { once: true });
      existing.addEventListener(
        'error',
        () => {
          loading = null;
          reject(new Error('GIS_LOAD'));
        },
        { once: true },
      );
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = finish;
    script.onerror = () => {
      loading = null;
      reject(new Error('GIS_LOAD'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

export async function bindGoogleIdentityButton(options: {
  clientId: string;
  host: HTMLElement;
  onCredential: (idToken: string) => void;
}): Promise<boolean> {
  const clientId = options.clientId.trim();
  if (!clientId) return false;
  try {
    await loadGoogleIdentityScript();
  } catch {
    return false;
  }
  const gis = window.google?.accounts?.id;
  if (!gis) return false;
  gis.initialize({
    auto_select: false,
    callback: (response) => {
      const token = response.credential?.trim();
      if (token) options.onCredential(token);
    },
    cancel_on_tap_outside: true,
    client_id: clientId,
    context: 'signin',
    itp_support: true,
    ux_mode: 'popup',
  });
  options.host.replaceChildren();
  gis.renderButton(options.host, {
    shape: 'circle',
    size: 'large',
    theme: 'outline',
    type: 'icon',
    width: 56,
  });
  return options.host.childElementCount > 0;
}
